@let presence = presence$ | async;
@let presenceTooltip = presenceTooltip$ | async;
@let userNotReadyReasonHint = userNotReadyReasonHint$ | async;
@let isManualPresenceSaving = isManualPresenceSaving$ | async;
@let userCanGetTasks = userCanGetTasks$ | async;
@let activeProfile = activeProfile$ | async;
@let isActivatingProfile = isActivatingProfile$ | async;
@let ronaStartedAt = ronaStartedAt$ | async;
@let afterCallWorkState = afterCallWorkState$ | async;
<span class="header-bar-nimbus-status-bar" [class]="statusIndicator$ | async"></span>
<button
  type="button"
  class="header-bar-presence-trigger"
  aria-haspopup="true"
  [attr.aria-expanded]="(isPanelOpen$ | async) ?? false"
  (click)="togglePanel()"
>
  <span class="header-bar-presence-menu-slot">
    <span class="header-bar-presence-avatar">
      <nimbus-avatar
        class="header-bar-presence-avatar-image"
        [imageSrc]="profilePicture"
        [name]="name"
        [initials]="name | initials"
      ></nimbus-avatar>
      @if (presence) {
        <span class="header-bar-presence-avatar-indicator" [ngClass]="presence.availability | lowercase">
          @if (!!presenceTooltip) {
            <nimbus-tooltip [text]="presenceTooltip" [ariaLabel]="presenceTooltip" position="bottom"></nimbus-tooltip>
          }
        </span>
      }
    </span>
  </span>
  <span class="header-bar-presence-details">
    <span class="header-bar-presence-name">
      {{ name }}
      <nimbus-tooltip [text]="name" [ariaLabel]="name"></nimbus-tooltip>
    </span>
    @if (ronaStartedAt) {
      <span class="header-bar-presence-state rona">
        <span class="header-bar-presence-state-label">{{ TranslationKeys.GeneralRona | translate }}</span>
        <span class="header-bar-presence-state-timer">{{
          baseDate$ | async | dateDifference: ronaStartedAt | durationFormat: 'mm:ss'
        }}</span>
      </span>
    } @else if (afterCallWorkState) {
      <span class="header-bar-presence-state acw">
        <span class="header-bar-presence-state-label">
          @switch (afterCallWorkState.state) {
            @case (UserInteractionOutcome.InAfterCallWork) {
              {{ TranslationKeys.DutyAcw | translate }}
            }
            @case (UserInteractionOutcome.InExtendedAfterCallWork) {
              {{ TranslationKeys.DutyExtendedAcw | translate }}
            }
          }
        </span>
        <span class="header-bar-presence-state-timer">
          {{ afterCallWorkState.afterCallWorkUntil | dateDifference: (baseDate$ | async) | durationFormat: 'mm:ss' }}
        </span>
      </span>
    } @else {
      <span class="header-bar-presence-state" [class.active]="userCanGetTasks">
        <span class="header-bar-presence-state-label">
          {{ (userCanGetTasks ? TranslationKeys.GeneralReachabilityReady : TranslationKeys.GeneralReachabilityNotReady) | translate }}
          @if (activeProfile) {
            &middot;
            {{ activeProfile.name }}
          }
          <nimbus-tooltip
            [text]="
              ((userCanGetTasks ? TranslationKeys.GeneralReachabilityReady : TranslationKeys.GeneralReachabilityNotReady) | translate) +
              (activeProfile ? ' · ' + activeProfile.name : '') +
              (userNotReadyReasonHint ? ' · ' + userNotReadyReasonHint : '')
            "
          ></nimbus-tooltip>
        </span>
      </span>
    }
  </span>
</button>
<div class="header-bar-presence-actions">
  @if (ronaStartedAt) {
    <button
      type="button"
      class="header-bar-presence-action header-bar-presence-action-stop"
      [disabled]="ronaStopDisabled$ | async"
      (click)="stopRona()"
    >
      <i [nimbusIcon]="ICONS.CLOSE"></i>
      <nimbus-tooltip [text]="TranslationKeys.GeneralStop | translate"></nimbus-tooltip>
    </button>
  } @else if (afterCallWorkState) {
    @if (afterCallWorkState.afterCallWorkAllowToExtend) {
      <button
        type="button"
        class="header-bar-presence-action header-bar-presence-action-extend"
        [disabled]="acwExtendDisabled$ | async"
        (click)="extendAcw(afterCallWorkState)"
      >
        <i [nimbusIcon]="ICONS.CLOCK_ADD"></i>
        <nimbus-tooltip [text]="TranslationKeys.GeneralExtend | translate"></nimbus-tooltip>
      </button>
    }
    @if (afterCallWorkState.afterCallWorkAllowToStop) {
      <button
        type="button"
        class="header-bar-presence-action header-bar-presence-action-stop"
        [disabled]="acwStopDisabled$ | async"
        (click)="stopAcw(afterCallWorkState)"
      >
        <i [nimbusIcon]="ICONS.CLOSE"></i>
        <nimbus-tooltip [text]="TranslationKeys.GeneralStop | translate"></nimbus-tooltip>
      </button>
    }
  }
  <!-- Span avoids a redundant tab stop and axe's aria-hidden-focus rule. -->
  <span class="header-bar-presence-dropdown-trigger" aria-hidden="true" (click)="togglePanel()">
    <i class="header-bar-presence-dropdown-trigger-icon" [nimbusIcon]="ICONS.CHEVRON_DOWN"></i>
  </span>
</div>
<span cdkOverlayOrigin #panelOrigin="cdkOverlayOrigin" class="header-bar-presence-panel-anchor"></span>

<ng-template
  cdkConnectedOverlay
  [cdkConnectedOverlayOrigin]="panelOrigin"
  [cdkConnectedOverlayPositions]="panelPositions"
  [cdkConnectedOverlayOpen]="(isPanelOpen$ | async) ?? false"
  [cdkConnectedOverlayHasBackdrop]="true"
  cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
  (backdropClick)="closePanel()"
  (detach)="closePanel()"
>
  <div class="header-bar-presence-panel">
    <div class="header-bar-presence-panel-content">
      @if (hasMultipleResponsibilityProfiles$ | async) {
        <span class="header-bar-presence-panel-group-label">{{ TranslationKeys.HeaderBarDutyProfileTitle | translate }}</span>
        <!-- Only profile rows scroll; labels and footer stay visible. -->
        <div class="header-bar-presence-panel-scroll">
          @for (profile of profiles$ | async; track profile.id) {
            <button
              type="button"
              class="header-bar-presence-panel-row"
              [class.selected]="activeProfile?.id === profile.id"
              [disabled]="isActivatingProfile"
              (click)="selectResponsibilityProfile(profile)"
            >
              <span class="state">
                <span class="state-indicator" [class.available]="profile.isDuty" [class.offline]="!profile.isDuty"></span>
                <span class="state-label">
                  {{ profile.name }}
                  <nimbus-tooltip [text]="profile.name" [ariaLabel]="profile.name"></nimbus-tooltip>
                </span>
              </span>
              @if (activeProfile?.id === profile.id) {
                <i class="state-selected" [nimbusIcon]="ICONS.CHECK"></i>
              }
            </button>
          }
        </div>
      }
      @if (isManualPresenceEnabled$ | async) {
        <span class="header-bar-presence-panel-group-label">{{ TranslationKeys.HeaderBarEmergencyPresenceTitle | translate }}</span>
        @for (status of manualPresenceOptions; track status) {
          <button
            type="button"
            class="header-bar-presence-panel-row"
            [class.selected]="presence?.availability === status"
            [disabled]="isManualPresenceSaving"
            (click)="selectManualPresence(status)"
          >
            <span class="state">
              <span class="state-indicator" [ngClass]="status | lowercase"></span>
              <span class="state-label">{{ manualPresenceLabel(status) | translate }}</span>
            </span>
            @if (presence?.availability === status) {
              <i class="state-selected" [nimbusIcon]="ICONS.CHECK"></i>
            }
          </button>
        }
      }
      <div class="header-bar-presence-panel-footer">
        <button type="button" class="header-bar-presence-panel-row" (click)="openPreferences()">
          <span class="state">
            <i class="state-icon" [nimbusIcon]="ICONS.SETTINGS"></i>
            <span class="state-label">{{ TranslationKeys.HeaderBarPreferences | translate }}</span>
          </span>
        </button>
        @if (canSignOut$ | async) {
          <button type="button" class="header-bar-presence-panel-row header-bar-presence-panel-row-sign-out" (click)="signOut()">
            <span class="state">
              <i class="state-icon" [nimbusIcon]="ICONS.LOGOUT"></i>
              <span class="state-label">{{ TranslationKeys.GeneralSignout | translate }}</span>
            </span>
          </button>
        }
      </div>
    </div>
  </div>
</ng-template>
